import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { runningPath } from '../utils/paths';
import { formatScheduleDateTime, toScheduleInputValue } from '../utils/scheduleTimeZone';
import './RunningPracticeScheduling.css';

const toApiDateTime = (localValue) => {
  if (!localValue) return null;
  // datetime-local → yyyy-MM-dd'T'HH:mm:ss
  return localValue.length === 16 ? `${localValue}:00` : localValue;
};


const EMPTY_FORM = {
  title: '',
  description: '',
  practiceDate: '',
  routeId: '',
  gatheringLocation: '',
  gatheringNotes: '',
  runnerCapacity: '',
};

const RunningPracticeScheduling = () => {
  const { id: clubId } = useParams();
  const { user, isAdmin } = useAuth();
  const [club, setClub] = useState(null);
  const [practices, setPractices] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [canManage, setCanManage] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const clubData = await api.getClubById(clubId);
      setClub(clubData);
      const isOwner = user?.id && clubData.adminUserId === user.id;
      const manage = !!(isOwner || isAdmin());
      setCanManage(manage);
      const [practiceData, routeData] = await Promise.all([
        api.getRunningPractices(clubId),
        api.getRunningRoutes(clubId, true),
      ]);
      setPractices(Array.isArray(practiceData) ? practiceData : []);
      setRoutes(Array.isArray(routeData) ? routeData : []);
    } catch (err) {
      setError(err.message || 'Failed to load practices');
    } finally {
      setLoading(false);
    }
  }, [clubId, user?.id, isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const startEdit = (practice) => {
    setEditingId(practice.id);
    setForm({
      title: practice.title || '',
      description: practice.description || '',
      practiceDate: toScheduleInputValue(practice.practiceDate),
      routeId: practice.routeId || '',
      gatheringLocation: practice.gatheringLocation || '',
      gatheringNotes: practice.gatheringNotes || '',
      runnerCapacity: practice.runnerCapacity != null ? String(practice.runnerCapacity) : '',
    });
  };

  const buildPayload = () => ({
    title: form.title.trim(),
    description: form.description.trim() || null,
    practiceDate: toApiDateTime(form.practiceDate),
    routeId: form.routeId || null,
    gatheringLocation: form.gatheringLocation.trim(),
    gatheringNotes: form.gatheringNotes.trim() || null,
    runnerCapacity: form.runnerCapacity ? Number(form.runnerCapacity) : null,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user?.id) return;
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const payload = buildPayload();
      if (editingId) {
        await api.updateRunningPractice(editingId, payload, user.id);
        setSuccess('Practice updated');
      } else {
        await api.createRunningPractice(clubId, payload, user.id);
        setSuccess('Practice published');
      }
      resetForm();
      await load();
    } catch (err) {
      setError(err.message || 'Failed to save practice');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (practiceId) => {
    if (!window.confirm('Cancel this practice?')) return;
    try {
      await api.cancelRunningPractice(practiceId, user.id);
      await load();
    } catch (err) {
      setError(err.message || 'Failed to cancel practice');
    }
  };

  if (loading) return <div className="loading">Loading practices...</div>;

  return (
    <div className="running-practice-sched">
      <div className="running-practice-sched__head">
        <p className="running-practice-sched__eyebrow">
          <Link to={runningPath(`/clubs/${clubId}`)}>{club?.name || 'Club'}</Link>
        </p>
        <h1>Group practices</h1>
        <p>Set timing, gathering location, and the route runners will practice on.</p>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {canManage && (
        <form className="running-practice-sched__form" onSubmit={handleSubmit}>
          <h2>{editingId ? 'Edit practice' : 'Publish a practice'}</h2>
          <label>
            Title
            <input name="title" value={form.title} onChange={onChange} required maxLength={100} />
          </label>
          <label>
            Date &amp; time
            <input
              name="practiceDate"
              type="datetime-local"
              value={form.practiceDate}
              onChange={onChange}
              required
            />
          </label>
          <label>
            Gathering location
            <input
              name="gatheringLocation"
              value={form.gatheringLocation}
              onChange={onChange}
              required
              placeholder="Parking lot, trailhead, track gate..."
            />
          </label>
          <label>
            Gathering notes
            <textarea
              name="gatheringNotes"
              value={form.gatheringNotes}
              onChange={onChange}
              rows={2}
              placeholder="Arrive 10 min early, bring water..."
            />
          </label>
          <label>
            Route
            <select name="routeId" value={form.routeId} onChange={onChange}>
              <option value="">Select a published route</option>
              {routes.map((route) => (
                <option key={route.id} value={route.id}>
                  {route.title}
                  {route.distanceKm != null ? ` (${route.distanceKm} km)` : ''}
                </option>
              ))}
            </select>
          </label>
          {routes.length === 0 && (
            <p className="running-practice-sched__hint">
              <Link to={runningPath(`/clubs/${clubId}/routes`)}>Publish a route</Link> first so
              runners can select a track.
            </p>
          )}
          <label>
            Description
            <textarea name="description" value={form.description} onChange={onChange} rows={3} />
          </label>
          <label>
            Runner capacity (optional)
            <input
              name="runnerCapacity"
              type="number"
              min="1"
              value={form.runnerCapacity}
              onChange={onChange}
            />
          </label>
          <div className="running-practice-sched__form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : editingId ? 'Save changes' : 'Publish practice'}
            </button>
            {editingId && (
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Cancel edit
              </button>
            )}
          </div>
        </form>
      )}

      <div className="running-practice-sched__list">
        {practices.length === 0 && <p>No practices yet.</p>}
        {practices.map((practice) => (
          <article key={practice.id} className="running-practice-sched__card">
            <div>
              <h3>
                {practice.title}
                {practice.status === 'CANCELLED' && (
                  <span className="running-practice-sched__badge">Cancelled</span>
                )}
                {practice.status === 'COMPLETED' && (
                  <span className="running-practice-sched__badge">Completed</span>
                )}
              </h3>
              <p>{formatScheduleDateTime(practice.practiceDate)}</p>
              <p>Meet: {practice.gatheringLocation}</p>
              {practice.route?.title && <p>Route: {practice.route.title}</p>}
            </div>
            <div className="running-practice-sched__actions">
              <Link to={runningPath(`/practices/${practice.id}`)} className="btn btn-primary">
                Open
              </Link>
              {canManage && practice.status === 'SCHEDULED' && (
                <>
                  <button type="button" className="btn btn-secondary" onClick={() => startEdit(practice)}>
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => handleCancel(practice.id)}
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};

export default RunningPracticeScheduling;
